/**
 * @module GlobalStore
 * @description Store Zustand para gerenciar dados globais não-persistentes da aplicação.
 * Armazena variáveis em memória que são perdidas ao recarregar a página.
 *
 * @requires zustand
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * @typedef {Object} GlobalStoreState
 * @property {Object} data - Objeto contendo todas as variáveis globais
 * @property {Function} setStore - Define valor de uma variável (varName, value)
 * @property {Function} getStore - Obtém valor de uma variável (varName)
 * @property {Function} clearStore - Remove uma variável específica (varName)
 * @property {Function} clearAllStore - Remove todas as variáveis
 */

/**
 * Store Zustand para armazenamento global não-persistente.
 *
 * @constant
 * @type {import('zustand').UseBoundStore<GlobalStoreState>}
 *
 * @example
 * // Armazenar e recuperar dados globais
 * import { useGlobalStore } from '@welm/stores/globalStore';
 *
 * function MeuComponente() {
 *   const setValue = useGlobalStore(s => s.setStore);
 *   const getValue = useGlobalStore(s => s.getStore);
 *   const clear = useGlobalStore(s => s.clearStore);
 *
 *   // Salvar valor
 *   setStore('theme', 'dark');
 *
 *   // Recuperar valor
 *   const theme = getStore('theme');
 *
 *   // Uso reativo
 *   const theme = useGlobalStore(s => s.data['theme']);
 * 
 * 
 *   return <div>Theme: {theme}</div>;
 * 
 * 
 *   // Remover variável
 *   clear('theme');
 * 
 *   // limpar todo o conteudo
 *   clearAllStore();
 * }
 *
 * @remarks
 * - **Não-persistente**: Dados são perdidos ao recarregar a página
 * - **Singleton**: Store compartilhada globalmente
 * - **Uso interno**: Usada principalmente pelo hook useStorage
 */
export const useGlobalStore = create(
  subscribeWithSelector((set, get) => ({
    data: {},
    setValue: (key, value) =>
      set((state) => ({
        data: {
          ...state.data,
          [key]: value
        },
      })),
    getValue: (key) => get().data[key],
    clear: (key) => {
      set((state) => {
        const newData = { ...state.data };
        delete newData[key];
        return { data: newData };
      })
    },
    clearAll: () => {
      set({ data: {} })
    }
  }))
);
